// libraries
import { useEffect, useState, useRef } from "react"
import Skeleton from "react-loading-skeleton"
import moment from "moment"

// helpers
import Api from "../../../../_helpers/Api"

// providers
import { useAuthDataContext } from "@/_providers/Auth"
import { trans } from "@/_providers/Translation"

// components
import GroupedBarChart from "../../../partials/GroupedBarChart"
import { getPaletteColor } from "../../../partials/chartTheme"
import { Button } from "@mui/material"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DateRange from "@/components/partials/DateRange"
import filterImage from "@/assets/img/icons/filter.svg"
import DatePicker from "@/components/partials/Date"

const INITIAL_FILTERS = {
  customer_id: "",
  date_from: null,
  date_to: null,
}

export default function SentMessagesChart() {
  const SERIES_CONFIG = [
    { key: "sms", label: "SMS", ...getPaletteColor(2) },
    { key: "viber", label: "Viber", ...getPaletteColor(0) },
    { key: "whatsapp", label: "WhatsApp", ...getPaletteColor(1) },
    { key: "email", label: "Email", ...getPaletteColor(2) },
  ]

  const { lang } = useAuthDataContext()

  const [chartData, setChartData] = useState([])
  const [customers, setCustomers] = useState([])
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [loading, setLoading] = useState(true)

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      loadData()
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters])

  const loadData = () => {
    setLoading(true)
    Api.get(
      `dashboard/sent-messages?customer_id=${filters.customer_id}${filters.date_from ? `&date_from=${filters.date_from || ""}` : ""}${filters.date_from ? `&date_to=${moment(filters.date_from).add(7, 'days').format('YYYY-MM-DD') || ""}` : ""}`,
    )
      .then((res) => {
        const dailyBreakdown = (res.data?.daily_breakdown || []).map(
          (item) => ({
            ...item,
            day_name_translated: moment(item.date, "YYYY-MM-DD")
              .locale(lang())
              .format("ddd"),
          }),
        )
        setChartData(dailyBreakdown)
        setCustomers(res.data?.customers || [])
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false))
  }

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function getAppliedFiltersCount() {
    let count = 0
    if (filters.customer_id) count++
    if (filters.date_from || filters.date_to) count++
    return count
  }

  function getIfAnyMessages() {
    return chartData.some(
      (item) =>
        item.sms > 0 || item.viber > 0 || item.whatsapp > 0 || item.email > 0,
    )
  }

  const labels = chartData.map((item) => item.day_name_translated)

  const series = SERIES_CONFIG.map((s) => ({
    name: s.label,
    color1: s.color1,
    color2: s.color2,
    data: chartData.map((item) => item[s.key] || 0),
  }))

  return (
    <div className="messages-chart">
      <div className="head">
        <h3>{trans("tabs.sendMessages")}</h3>
        <div className="right">
          <Button
            onClick={(e) => {
              compactFilterLayoutRef.current.open(e)
            }}
          >
            <img src={filterImage} alt="Filter" />
            {getAppliedFiltersCount() ? (
              <i>{getAppliedFiltersCount()}</i>
            ) : (
              <></>
            )}
          </Button>
          <CompactFilterLayout
            ref={compactFilterLayoutRef}
            appliedFiltersCount={getAppliedFiltersCount()}
            onClearClick={() => setFilters(INITIAL_FILTERS)}
          >
            <div className="row">
              <div className="col">
                <DatePicker
                  date={filters.date_from || ""}
                  onDateChange={(date) => handleFilterChange("date_from", date)}
                  placeholder={trans("labels.beginingOf7dayPariod")}
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <select
                  name="customer_id"
                  value={filters.customer_id}
                  onChange={(e) =>
                    handleFilterChange("customer_id", e.target.value)
                  }
                >
                  <option value="">{trans("random.allCustomers")}</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.full_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CompactFilterLayout>
        </div>
      </div>
      <hr />
      {loading ? (
        <Skeleton height={360} count={1} />
      ) : !getIfAnyMessages() ? (
        <div
          className="alert warning"
          style={{ marginBottom: 0, width: "100%" }}
        >
          {trans("messages.noDataFound")}
        </div>
      ) : (
        <GroupedBarChart labels={labels} series={series} roundedBottom />
      )}
    </div>
  )
}
